import { useEffect, useMemo, useState } from "react";
import { useSession } from "@clerk/clerk-react";
import { toast } from "react-toastify";
import * as Sentry from "@sentry/react";
import { CheckCircle, MagnifyingGlass, Warning, XCircle } from "@phosphor-icons/react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "components/ui/dialog";
import { Button } from "components/ui/button";
import { Checkbox } from "components/ui/checkbox";
import { Input } from "components/ui/input";
import { Label } from "components/ui/label";
import { API_URL } from "constants/index";
import { fetchWithAuth } from "shared/api/fetch-with-auth";
import { YallyCompany } from "shared/types/yally-company";

interface LedgerAccount {
  id: number;
  code: string;
  name: string;
}

interface RolloutResult {
  companyId: number;
  companyName: string;
  status: "created" | "reused" | "skipped" | "failed";
  reason?: string;
}

interface CreateYallyProductModalProps {
  isOpen: boolean;
  onClose: () => void;
  /** All Yally companies; the ones with an Odoo link are selected by default */
  companies: YallyCompany[];
  /** Called after a rollout so the overview can refresh */
  onCreated: () => void;
}

/** What the rollout fixes for every company — shown so the user knows what is created. */
const FIXED_SETTINGS = [
  "Productsoort: Dienst",
  "Kan verkocht worden, niet gekocht",
  "Abonnementen + Can be Indexed",
  "Verkoop BTW 0%",
  "Verkoopprijs 0,00 €",
  "Bedrijf en omzetrekening per vennootschap",
];

/**
 * Create a new Yally product (name + omzetrekening) and roll it out to the selected
 * vennootschappen. The definition is stored as a sjabloon, so vennootschappen created
 * later get this product too.
 */
export const CreateYallyProductModal = ({
  isOpen,
  onClose,
  companies,
  onCreated,
}: CreateYallyProductModalProps) => {
  const { session } = useSession();
  const [name, setName] = useState("");
  const [ledgerAccounts, setLedgerAccounts] = useState<LedgerAccount[]>([]);
  const [selectedCode, setSelectedCode] = useState<string | null>(null);
  const [search, setSearch] = useState("");
  const [showAllAccounts, setShowAllAccounts] = useState(false);
  const [selectedCompanyIds, setSelectedCompanyIds] = useState<number[]>([]);
  const [isSaving, setIsSaving] = useState(false);
  const [results, setResults] = useState<RolloutResult[] | null>(null);

  // Only companies with an Odoo company can receive a product.
  const activeCompanies = useMemo(
    () => (companies || []).filter((company) => !(company as any).archived),
    [companies],
  );
  const eligibleCompanies = useMemo(
    () => activeCompanies.filter((company) => !!company.odooId),
    [activeCompanies],
  );
  const ineligibleCompanies = useMemo(
    () => activeCompanies.filter((company) => !company.odooId),
    [activeCompanies],
  );

  useEffect(() => {
    if (!isOpen) {
      setName("");
      setSelectedCode(null);
      setSearch("");
      setShowAllAccounts(false);
      setResults(null);
      return;
    }

    // Default: every eligible vennootschap is enabled.
    setSelectedCompanyIds(eligibleCompanies.map((company) => company.id));

    const fetchLedgerAccounts = async () => {
      try {
        const response = await fetchWithAuth(
          `${API_URL}/companies/ledger-accounts/templates`,
          { method: "GET" },
          session,
        );
        if (!response.ok) throw new Error(`Ledger accounts request failed: ${response.status}`);
        const data = await response.json();
        setLedgerAccounts(Array.isArray(data) ? data : []);
      } catch (error) {
        console.error("Error fetching ledger accounts:", error);
        toast.error("Kon de grootboekrekeningen niet ophalen");
        Sentry.captureException(error, {
          tags: { feature: "yallyProductCreate", method: "fetchLedgerAccounts" },
        });
      }
    };

    fetchLedgerAccounts();
  }, [isOpen, session, eligibleCompanies]);

  const visibleAccounts = useMemo(() => {
    const term = search.trim().toLowerCase();
    return ledgerAccounts
      .filter((account) => (showAllAccounts ? true : account.code?.startsWith("7")))
      .filter((account) =>
        term
          ? account.code?.toLowerCase().includes(term) || account.name?.toLowerCase().includes(term)
          : true,
      )
      .sort((a, b) => (a.code || "").localeCompare(b.code || ""))
      .slice(0, 200);
  }, [ledgerAccounts, search, showAllAccounts]);

  const selectedAccount = ledgerAccounts.find((account) => account.code === selectedCode) || null;
  const allCompaniesSelected = selectedCompanyIds.length === eligibleCompanies.length;

  const toggleCompany = (companyId: number) => {
    setSelectedCompanyIds((previous) =>
      previous.includes(companyId)
        ? previous.filter((id) => id !== companyId)
        : [...previous, companyId],
    );
  };

  const handleSubmit = async () => {
    if (!name.trim()) {
      toast.error("Geef een productnaam in");
      return;
    }
    if (!selectedCode) {
      toast.error("Kies een omzetrekening");
      return;
    }
    if (selectedCompanyIds.length === 0) {
      toast.error("Selecteer minstens één vennootschap");
      return;
    }

    setIsSaving(true);
    try {
      const response = await fetchWithAuth(
        `${API_URL}/companies/product-variant-templates/create-and-rollout`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            name: name.trim(),
            ledgerAccountCode: selectedCode,
            indexable: true,
            companyIds: selectedCompanyIds,
          }),
        },
        session,
      );

      if (!response.ok) {
        const body = await response.text();
        throw new Error(`Rollout failed (${response.status}): ${body}`);
      }

      const { rollout } = await response.json();
      setResults(rollout?.results ?? []);

      if (rollout?.failed > 0) {
        toast.warning(
          `Product aangemaakt voor ${rollout.created} vennootschappen, ${rollout.failed} mislukt`,
        );
      } else {
        toast.success(`Product aangemaakt voor ${rollout?.created ?? 0} vennootschappen`);
      }

      onCreated();
    } catch (error) {
      console.error("Error creating Yally product:", error);
      toast.error("Fout bij aanmaken van het product");
      Sentry.captureException(error, {
        tags: { feature: "yallyProductCreate", method: "handleSubmit" },
        extra: { name, ledgerAccountCode: selectedCode, companyCount: selectedCompanyIds.length },
      });
    } finally {
      setIsSaving(false);
    }
  };

  const statusIcon = (status: RolloutResult["status"]) => {
    if (status === "failed") return <XCircle size={16} className="text-brick-primary shrink-0" weight="fill" />;
    if (status === "skipped") return <Warning size={16} className="text-yellow-600 shrink-0" weight="fill" />;
    return <CheckCircle size={16} className="text-moss-primary shrink-0" weight="fill" />;
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      {/* flex + overflow-hidden instead of the default grid: a grid track sizes to its
          widest child, which pushed the inputs and buttons outside the panel. */}
      <DialogContent className="flex max-h-[85vh] flex-col overflow-hidden sm:max-w-xl">
        <DialogHeader className="shrink-0">
          <DialogTitle className="text-neutral-primary">Nieuw Yally product</DialogTitle>
          <DialogDescription className="text-neutral-secondary-mid">
            Het product wordt in Odoo aangemaakt voor de geselecteerde vennootschappen, en geldt
            ook voor vennootschappen die later worden toegevoegd.
          </DialogDescription>
        </DialogHeader>

        {results ? (
          <div className="min-w-0 flex-1 space-y-2 overflow-y-auto">
            {results.map((result) => (
              <div
                key={result.companyId}
                className="flex min-w-0 items-start gap-2 rounded-lg border border-neutral-primary/15 bg-white p-2.5 text-sm"
              >
                {statusIcon(result.status)}
                <div className="min-w-0">
                  <p className="font-medium text-neutral-primary">{result.companyName}</p>
                  <p className="text-xs text-neutral-secondary-mid">
                    {result.status === "created" && "Product aangemaakt"}
                    {result.status === "reused" && "Bestond al in Odoo — hergebruikt"}
                    {result.status === "skipped" && (result.reason || "Overgeslagen")}
                    {result.status === "failed" && (result.reason || "Mislukt")}
                  </p>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="min-w-0 flex-1 space-y-4 overflow-y-auto">
            <div className="space-y-1.5">
              <Label htmlFor="yally-product-name">Productnaam</Label>
              <Input
                id="yally-product-name"
                value={name}
                onChange={(event) => setName(event.target.value)}
                placeholder="bv. Huur attribuut - garagebox"
                disabled={isSaving}
              />
            </div>

            <div className="space-y-1.5">
              <div className="flex items-center justify-between gap-2">
                <Label>Omzetrekening</Label>
                <button
                  type="button"
                  onClick={() => setShowAllAccounts((previous) => !previous)}
                  className="shrink-0 text-xs font-medium text-moss-primary hover:underline"
                >
                  {showAllAccounts ? "Alleen 7-rekeningen" : "Toon alle rekeningen"}
                </button>
              </div>

              {selectedAccount && (
                <p className="truncate text-sm text-neutral-primary">
                  Gekozen: <span className="font-medium">{selectedAccount.code}</span> — {selectedAccount.name}
                </p>
              )}

              <div className="relative">
                <MagnifyingGlass
                  size={16}
                  className="absolute left-2.5 top-1/2 -translate-y-1/2 text-neutral-secondary-mid"
                />
                <Input
                  value={search}
                  onChange={(event) => setSearch(event.target.value)}
                  placeholder="Zoek op code of naam"
                  className="pl-8"
                  disabled={isSaving}
                />
              </div>

              <div className="min-w-0 max-h-40 divide-y divide-neutral-primary/10 overflow-y-auto rounded-lg border border-neutral-primary/15">
                {visibleAccounts.length === 0 ? (
                  <p className="p-3 text-sm text-neutral-secondary-mid">Geen rekeningen gevonden</p>
                ) : (
                  visibleAccounts.map((account) => (
                    <button
                      key={account.id}
                      type="button"
                      onClick={() => setSelectedCode(account.code)}
                      className={`flex w-full min-w-0 items-center gap-2 p-2 text-left text-sm hover:bg-moss-primary/5 ${
                        selectedCode === account.code ? "bg-moss-primary/10 font-medium" : ""
                      }`}
                    >
                      <span className="w-16 shrink-0 text-neutral-primary">{account.code}</span>
                      <span className="min-w-0 flex-1 truncate text-neutral-secondary-mid">{account.name}</span>
                    </button>
                  ))
                )}
              </div>
            </div>

            <div className="space-y-1.5">
              <div className="flex items-center justify-between gap-2">
                <Label>
                  Vennootschappen ({selectedCompanyIds.length}/{eligibleCompanies.length})
                </Label>
                <button
                  type="button"
                  onClick={() =>
                    setSelectedCompanyIds(
                      allCompaniesSelected ? [] : eligibleCompanies.map((company) => company.id),
                    )
                  }
                  className="shrink-0 text-xs font-medium text-moss-primary hover:underline"
                >
                  {allCompaniesSelected ? "Alles uitvinken" : "Alles aanvinken"}
                </button>
              </div>

              <div className="min-w-0 max-h-40 space-y-1 overflow-y-auto rounded-lg border border-neutral-primary/15 p-2">
                {eligibleCompanies.map((company) => (
                  <label
                    key={company.id}
                    className="flex min-w-0 cursor-pointer items-center gap-2 rounded-md p-1 text-sm hover:bg-moss-primary/5"
                  >
                    <Checkbox
                      checked={selectedCompanyIds.includes(company.id)}
                      onCheckedChange={() => toggleCompany(company.id)}
                      disabled={isSaving}
                    />
                    <span className="min-w-0 flex-1 truncate text-neutral-primary">{company.name}</span>
                  </label>
                ))}

                {ineligibleCompanies.map((company) => (
                  <div
                    key={company.id}
                    className="flex min-w-0 items-center gap-2 rounded-md p-1 text-sm opacity-60"
                    title="Geen Odoo-vennootschap gekoppeld"
                  >
                    <Checkbox checked={false} disabled />
                    <span className="min-w-0 flex-1 truncate text-neutral-secondary-mid">
                      {company.name} — geen Odoo-koppeling
                    </span>
                  </div>
                ))}
              </div>
            </div>

            <div className="min-w-0 rounded-lg border border-neutral-primary/15 bg-neutral-primary/5 p-3">
              <p className="mb-1 text-xs font-semibold text-neutral-primary">Wordt automatisch ingesteld</p>
              <ul className="space-y-0.5 text-xs text-neutral-secondary-mid">
                {FIXED_SETTINGS.map((setting) => (
                  <li key={setting}>• {setting}</li>
                ))}
              </ul>
            </div>
          </div>
        )}

        <DialogFooter className="shrink-0">
          {results ? (
            <Button onClick={onClose}>Sluiten</Button>
          ) : (
            <>
              <Button variant="outline" onClick={onClose} disabled={isSaving}>
                Annuleren
              </Button>
              <Button
                onClick={handleSubmit}
                disabled={isSaving || !name.trim() || !selectedCode || selectedCompanyIds.length === 0}
              >
                {isSaving
                  ? "Aanmaken…"
                  : `Aanmaken voor ${selectedCompanyIds.length} vennootschap${selectedCompanyIds.length === 1 ? "" : "pen"}`}
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default CreateYallyProductModal;
